import React, { useMemo } from "react";
import { Box, Typography, Card, CardContent, Grid } from "@mui/material";
import {
  AreaChart,
  Area,
  LineChart,
  Line,
  XAxis,
  YAxis,
  ResponsiveContainer,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { format } from "date-fns";

const ConnectionAnalytics = ({ userData, selectedStartDate, selectedEndDate }) => {
  // Mock connection data based on user registrations - in real app this would come from API
  const connectionData = useMemo(() => {
    // Generate mock connection data based on registration patterns
    const mockConnections = [
      { date: '2025-02-01', connections: "N.A.", avgTime: "N.A." },
      { date: '2025-02-02', connections: "N.A.", avgTime: "N.A." },
      { date: '2025-02-03', connections: "N.A.", avgTime: "N.A." },
      { date: '2025-02-04', connections: "N.A.", avgTime: "N.A." },
      { date: '2025-02-05', connections: "N.A.", avgTime: "N.A." },
      { date: '2025-02-22', connections: "N.A.", avgTime: "N.A." },
      { date: '2025-02-23', connections: "N.A.", avgTime: "N.A." },
      { date: '2025-02-24', connections: "N.A.", avgTime: "N.A." },
    ];

    // Filter by date range if provided
    if (selectedStartDate && selectedEndDate) {
      const startOfStartDate = new Date(selectedStartDate);
      startOfStartDate.setHours(0, 0, 0, 0);
      const endOfEndDate = new Date(selectedEndDate);
      endOfEndDate.setHours(23, 59, 59, 999);
      
      return mockConnections.filter((item) => {
        const itemDate = new Date(item.date);
        const itemDateTime = itemDate.getTime();
        const startDateTime = startOfStartDate.getTime();
        const endDateTime = endOfEndDate.getTime();
        
        return itemDateTime >= startDateTime && itemDateTime <= endDateTime;
      }).map(item => ({
        ...item,
        date: format(new Date(item.date), 'MMM dd')
      }));
    }

    return mockConnections.map(item => ({
      ...item,
      date: format(new Date(item.date), 'MMM dd')
    }));
  }, [selectedStartDate, selectedEndDate]);

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Daily Connections Area Chart */}
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              backgroundColor: "#1a1b23",
              border: "1px solid #333",
              height: "400px",
            }}
          >
            <CardContent sx={{ height: "100%", position: "relative" }}>
              <Typography variant="h6" sx={{ color: "white", mb: 1, fontSize: "20px", fontWeight: "600" }}>
                Daily Connections
              </Typography>
              <Typography variant="body2" sx={{ color: "#9e9e9e", mb: 3, fontSize: "14px" }}>
                Number of users connecting each day
              </Typography>
              
              {/* Area Chart using Recharts */}
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart
                  data={connectionData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" opacity={0.3} />
                  <XAxis 
                    dataKey="date" 
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#9e9e9e", fontSize: 12 }}
                  />
                  <YAxis 
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#9e9e9e", fontSize: 12 }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#1a1b23",
                      border: "1px solid #333",
                      borderRadius: "8px",
                      color: "white",
                      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                    }}
                    labelStyle={{ color: "#24b8e7" }}
                    formatter={(value, name) => [
                      value === "N.A." ? "N.A." : `${value} connection${value !== 1 ? 's' : ''}`,
                      "Connections"
                    ]}
                    labelFormatter={(label) => `Date: ${label}`}
                  />
                  <Area
                    type="monotone"
                    dataKey="connections"
                    stroke="#24b8e7"
                    strokeWidth={2}
                    fill="#24b8e7"
                    fillOpacity={0.3}
                  />
                </AreaChart>
              </ResponsiveContainer>
              
              {/* Show message if no data */}
              {connectionData.length === 0 && (
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    textAlign: "center",
                    color: "#9e9e9e",
                  }}
                >
                  <Typography variant="body2">
                    No connection data available for the selected period
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Average Connection Time Line Chart */}
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              backgroundColor: "#1a1b23",
              border: "1px solid #333",
              height: "400px",
            }}
          >
            <CardContent sx={{ height: "100%", position: "relative" }}>
              <Typography variant="h6" sx={{ color: "white", mb: 1, fontSize: "20px", fontWeight: "600" }}>
                Average Connection Time
              </Typography>
              <Typography variant="body2" sx={{ color: "#9e9e9e", mb: 3, fontSize: "14px" }}>
                Average time users spend connected (minutes)
              </Typography>
              
              {/* Line Chart using Recharts */}
              <ResponsiveContainer width="100%" height={280}>
                <LineChart
                  data={connectionData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" opacity={0.3} />
                  <XAxis 
                    dataKey="date" 
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#9e9e9e", fontSize: 12 }}
                  />
                  <YAxis 
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#9e9e9e", fontSize: 12 }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#1a1b23",
                      border: "1px solid #333",
                      borderRadius: "8px",
                      color: "white",
                      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                    }}
                    labelStyle={{ color: "#24b8e7" }}
                    formatter={(value, name) => [
                      value === "N.A." ? "N.A." : `${value} minutes`,
                      "Avg Time"
                    ]}
                    labelFormatter={(label) => `Date: ${label}`}
                  />
                  <Line
                    type="monotone"
                    dataKey="avgTime"
                    stroke="#4285F4"
                    strokeWidth={3}
                    dot={{ fill: "#4285F4", strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6, stroke: "#4285F4", strokeWidth: 2 }}
                  />
                </LineChart>
              </ResponsiveContainer>
              
              {/* Show message if no data */}
              {connectionData.length === 0 && (
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    textAlign: "center",
                    color: "#9e9e9e",
                  }}
                >
                  <Typography variant="body2">
                    No connection time data available for the selected period
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ConnectionAnalytics;